import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import IconArrowRight from 'src/assets/images/icons/chevron-right.svg';
import { useUserContext } from 'src/use-cases/user/userDetails';
import {
  IconContract,
  IconCreditTransactions,
  IconDiscount,
  IconExchange,
  IconInvoice,
  IconLegal,
  IconModelConfiguration,
  IconServiceProvider,
  IconUser,
  IconWorkflow,
} from '../../../atom/icons/settings';
import { SettingsMain } from '../SettingsMain';
import { MenuItem } from '../types';

export const SettingsFront: React.FC = () => {
  const { userDetails } = useUserContext();
  const userRoles = userDetails?.roles ?? [];
  const isCreditViewerRole = userRoles.includes('CREDIT_VIEWER');

  const menuItems: MenuItem[] = useMemo(() => {
    const menuItems = [];
    if (userDetails?.systemAdmin) {
      menuItems.push(
        ...[
          {
            label: 'User Details',
            info: 'Adjust the user permissions on the platform',
            path: '/settings/user-profile',
            icon: 'user',
          },
          {
            label: 'Legal Entities',
            info: 'Configure the billed and legal entities',
            path: '/settings/legal-entities',
            icon: 'legal',
          },
          {
            label: 'Service Providers',
            info: 'Manage the service providers list',
            path: '/settings/service-providers',
            icon: 'service-provider',
          },
          {
            label: 'Exchange Rates',
            info: 'Setup the exchange rates',
            path: '/settings/exchange-rate',
            icon: 'exchange',
          },
          {
            label: 'Audit Workflow Configurations',
            info: 'Manage the audit workflow configurations',
            path: '/settings/audit-workflow',
            icon: 'workflow',
          },
        ]
      );
    }

    if (userDetails?.systemAdmin || isCreditViewerRole) {
      menuItems.push({
        label: 'Credit Transactions',
        info: 'See all your credit transactions here',
        path: '/settings/credit-transactions',
        icon: 'credit-transactions',
      });
    }

    if (userDetails?.systemAdmin) {
      menuItems.push(
        ...[
          {
            label: 'Charge Mappings',
            info: 'Adjust the invoice to charge domain mappings',
            path: '/settings/invoice-charge',
            icon: 'invoice',
          },
          {
            label: 'Model Configurations',
            info: 'Setup the invoice models',
            path: '/settings/model-configuration',
            icon: 'model-configuration',
          },
          {
            label: 'Contracts',
            info: 'Configure contract settings',
            path: '/settings/contracts',
            icon: 'contract',
          },
        ]
      );
    }

    if (userDetails?.systemAdmin) {
      menuItems.push({
        label: 'Discounts Mapping',
        info: 'Configure discounts mapping',
        path: '/settings/discounts',
        icon: 'discount',
      });
    }

    return menuItems;
  }, [userDetails, isCreditViewerRole]);

  return (
    <SettingsMain title='Settings' subtitle='Set your platform settings and preferences'>
      <div className='flex flex-col px-16 xl:px-24 overflow-auto h-full'>
        {menuItems.map((item) => (
          <div key={`path-${item.path}`} className='py-12 lg:py-16 border-b last:border-0 border-neutral-20'>
            <Link to={item.path} className='flex items-center gap-16 px-16 py-12 transition-all rounded-12 hover:bg-neutral-10'>
              <div className='w-24 h-24 flex items-center justify-center gap-8 text-neutral-60'>
                {item.icon === 'user' && <IconUser size={24} />}
                {item.icon === 'legal' && <IconLegal size={24} />}
                {item.icon === 'exchange' && <IconExchange size={24} />}
                {item.icon === 'invoice' && <IconInvoice size={24} />}
                {item.icon === 'credit-transactions' && <IconCreditTransactions size={24} />}
                {item.icon === 'model-configuration' && <IconModelConfiguration size={24} />}
                {item.icon === 'service-provider' && <IconServiceProvider size={24} />}
                {item.icon === 'workflow' && <IconWorkflow size={24} />}
                {item.icon === 'contract' && <IconContract size={24} />}
                {item.icon === 'discount' && <IconDiscount size={24} />}
              </div>
              <div className='flex flex-col gap-4'>
                <h2 className='text-14 lg:text-16 font-medium'>{item.label}</h2>
                {item.info && <p className='text-12 text-neutral-60'>{item.info}</p>}
              </div>
              <div className='ml-auto flex items-center justify-center'>
                <img src={IconArrowRight} alt='' />
              </div>
            </Link>
          </div>
        ))}
      </div>
    </SettingsMain>
  );
};
